import React from 'react';
import styled from 'styled-components';

import { useTranslate } from '@src/core/hooks';
import { useRequiredFormInputs } from '@src/features/orders/form-inputs';
import { TariffExtraInfoFormInputs } from '@src/features/orders/form-inputs/legacy/TariffExtraInfoFormInputs';
import FilledButton from '@src/components/reusable/button/FilledButton';
import { device } from '@src/common/breakpoints';
import { BodyLarge, BodyLargeMedium, BodySmallMedium, Heading4, Heading6 } from '@src/fonts/typography/Typography';
import { LayoutColors } from '@src/core/theme';

export const TariffExtraInfoCollect = ({ proceedToNextStage }: TariffExtraInfoCollectProps) => {
  const { inputs, handleInputDataToSend, handleSubmit, isActionButtonDisabled, isSending, redirect } =
    useRequiredFormInputs(proceedToNextStage);
  const { i18nElement } = useTranslate();

  return (
    <TariffExtraInfoContainer>
      <HeaderTitle>{i18nElement('checkout_buyerInfoCollectHeaderTitle')}</HeaderTitle>
      <ContainerSubtitleText>{i18nElement('checkout_buyerInfoCollectSubtitle')}</ContainerSubtitleText>
      {inputs.map((formInputs, index) => (
        <TariffExtraInfoFormInputs
          index={index}
          handleInputDataToSend={handleInputDataToSend}
          formInputs={formInputs}
          key={index}
        />
      ))}
      <ContainerActions>
        <FilledButton
          disabled={isActionButtonDisabled}
          bgColor={LayoutColors.successDark}
          loading={isSending}
          onClick={handleSubmit}
        >
          {i18nElement('checkout_buyerInfoCollectProceed')}
        </FilledButton>
        <StepBackButton onClick={() => redirect()}>{i18nElement('checkout_buyerInfoCollectStepback')}</StepBackButton>
      </ContainerActions>
    </TariffExtraInfoContainer>
  );
};

interface TariffExtraInfoCollectProps {
  proceedToNextStage: () => void;
}

const TariffExtraInfoContainer = styled.div`
  width: 100%;
  max-width: 800px;
  padding: 16px;
  display: flex;
  flex-flow: column;
  margin: 0 auto;
  background-color: ${LayoutColors.whiteDark};
  border-radius: 8px;
  gap: 16px;

  ${device.smallTablet} {
    padding: 24px;
    gap: 32px;
  }
`;

const HeaderTitle = styled.div`
  ${Heading6};
  color: ${LayoutColors.blackDark};

  ${device.smallTablet} {
    ${Heading4};
    color: ${LayoutColors.blackDark};
  }
`;

export const ContainerSubtitleText = styled.div`
  ${BodySmallMedium};
  color: ${LayoutColors.blackMain};

  ${device.smallTablet} {
    ${BodyLarge}
    color: ${LayoutColors.blackMain};
  }
`;

const ContainerActions = styled.div`
  display: flex;
  flex-flow: column;
  gap: 4px;
  margin-top: 24px;
`;

const StepBackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  background-color: transparent;
  ${BodyLargeMedium};
  font-weight: 700;
  padding-top: 24px;
  border-style: none;
`;
